import { Controller } from "@hotwired/stimulus"

const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp", "application/pdf"]
const MAX_BYTES = 15 * 1024 * 1024 // 15 MB

// Handles fire-and-forget receipt uploads from the drop zone or browse button.
// Each uploaded file gets an immediate status item in the statusList target.
// The actual scan result appears later via Turbo Stream on the Inbox page.
export default class extends Controller {
  static targets = ["fileInput", "statusList"]
  static values  = { url: { type: String, default: "/receipt_scans" } }

  // ── Drag-and-drop ───────────────────────────────────────────────────────

  dragover(event) {
    event.preventDefault()
    this.element.dataset.dragover = "true"
  }

  dragleave() {
    delete this.element.dataset.dragover
  }

  drop(event) {
    event.preventDefault()
    delete this.element.dataset.dragover
    const files = Array.from(event.dataTransfer.files)
    this.#uploadAll(files)
  }

  // ── File input ──────────────────────────────────────────────────────────

  chooseFiles() {
    this.fileInputTarget.click()
  }

  filesSelected(event) {
    const files = Array.from(event.target.files)
    this.#uploadAll(files)
    // Reset so the same file can be re-selected if needed.
    event.target.value = ""
  }

  // ── private ─────────────────────────────────────────────────────────────

  #uploadAll(files) {
    files.forEach(file => this.#upload(file))
  }

  async #upload(file) {
    if (!ALLOWED_TYPES.includes(file.type)) {
      const message = "Unsupported file type"
      this.#addStatusItem(file.name, "error", message)
      this.#notify("error", message)
      return
    }

    if (file.size > MAX_BYTES) {
      const message = "File exceeds 15 MB limit"
      this.#addStatusItem(file.name, "error", message)
      this.#notify("error", message)
      return
    }

    const item = this.#addStatusItem(file.name, "uploading", "Receipt is uploading")

    const formData = new FormData()
    formData.append("file", file)

    try {
      const response = await fetch(this.urlValue, {
        method:  "POST",
        body:    formData,
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content }
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        this.#updateStatusItem(item, "error", data.error || "Upload failed")
        return
      }

      const data = await response.json().catch(() => ({}))
      if (data.scan_status === "pending") {
        this.#updateStatusItem(item, "queued", data.message || "Receipt queued for scanning")
      } else if (data.scan_status === "not_scanned") {
        this.#updateStatusItem(item, "manual", data.message || "Receipt uploaded and ready for manual entry")
      } else {
        this.#updateStatusItem(item, "error", "Upload failed — please try again")
      }
    } catch {
      this.#updateStatusItem(item, "error", "Network error — please try again")
    }
  }

  #addStatusItem(filename, state, message = null) {
    if (!this.hasStatusListTarget) return null

    const li = document.createElement("li")
    li.className = "flex items-center gap-2 rounded-md px-3 py-2 text-sm"
    li.dataset.filename = filename
    li.innerHTML = this.#statusHTML(filename, state, message)
    this.statusListTarget.appendChild(li)

    // Keep errors visible; remove only successful statuses after 8 seconds.
    if (this.#isSuccessful(state)) {
      setTimeout(() => li.remove(), 8000)
    }

    return li
  }

  #updateStatusItem(item, state, message) {
    this.#notify(state === "error" ? "error" : "success", message)
    if (!item) return

    item.innerHTML = this.#statusHTML(item.dataset.filename, state, message)

    if (this.#isSuccessful(state)) {
      setTimeout(() => item.remove(), 8000)
    }
  }

  #notify(type, message) {
    window.dispatchEvent(new CustomEvent("notification:show", {
      detail: {
        title: type === "success" ? "Receipt uploaded" : "Receipt upload failed",
        message,
        type
      }
    }))
  }

  #statusHTML(filename, state, message) {
    const icon = this.#iconFor(state)
    const colorClass = state === "error"
      ? "bg-red-50 text-red-700"
      : state === "queued"
        ? "bg-green-50 text-green-700"
        : "bg-gray-50 text-gray-600"
    const statusLabel = {
      uploading: "Uploading",
      queued: "Queued for scanning",
      manual: "Ready for manual entry",
      error: "Upload failed"
    }[state]

    return `
      <span class="inline-flex items-center gap-2 w-full rounded ${colorClass} px-2 py-1">
        ${icon}
        <span class="font-medium truncate">${this.#escHtml(filename)}</span>
        <span>${statusLabel}: ${this.#escHtml(message || "")}</span>
      </span>
    `
  }

  #iconFor(state) {
    if (state === "uploading") {
      return `<svg aria-hidden="true" class="size-4 animate-spin shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
      </svg>`
    }
    if (state === "queued") {
      return `<svg aria-hidden="true" class="size-4 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clip-rule="evenodd"/>
      </svg>`
    }
    if (state === "manual") {
      return `<svg aria-hidden="true" class="size-4 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-11.25a.75.75 0 0 0-1.5 0v3.69c0 .2.08.39.22.53l2.5 2.5a.75.75 0 0 0 1.06-1.06l-2.28-2.28V6.75Z" clip-rule="evenodd"/>
      </svg>`
    }

    // error
    return `<svg aria-hidden="true" class="size-4 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
      <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z" clip-rule="evenodd"/>
    </svg>`
  }

  #isSuccessful(state) {
    return state === "queued" || state === "manual"
  }

  #escHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
